// src/pages/ListaDECA.jsx - DISEÑO PROFESIONAL
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";

export default function ListaDECA() {
  const { user, companyId } = useAuth();
  const navigate = useNavigate();
  const [decaDocuments, setDecaDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  useEffect(() => {
    if (!user || !companyId) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, "decas"),
        where("companyId", "==", companyId)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        try {
          const docs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          docs.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return dateB - dateA;
          });
          
          setDecaDocuments(docs);
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error("Error processing DECA documents:", err);
          setError(err.message);
          setLoading(false);
        }
      }, (error) => {
        console.error("Error loading DECA documents:", error);
        setError(error.message);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error("Error setting up DECA query:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [user, companyId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (err) {
      return "N/A";
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return "N/A";
    }
  };

  // Filtrar documentos según búsqueda y estado
  const filteredDocuments = decaDocuments.filter(doc => {
    const matchesSearch = searchTerm === "" || 
      doc.codigoDECA?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.cargador?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.transportista?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.origen?.ciudad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.destino?.ciudad?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "todos" || doc.estado === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (estado) => {
    switch (estado) {
      case 'generado': return { bg: '#dcfce7', color: '#15803d', text: 'Generado' };
      case 'enviado': return { bg: '#dbeafe', color: '#2563eb', text: 'Enviado' };
      case 'completado': return { bg: '#e0e7ff', color: '#7c3aed', text: 'Completado' };
      default: return { bg: '#fef3c7', color: '#d97706', text: 'Pendiente' };
    }
  };
  
  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <span className="badge">DECA</span>
        </div>
        <h1 className="title">Documentos DECA</h1>
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 0',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div className="spinner" style={{ margin: '0 auto 24px' }}></div>
          <h3 style={{ color: '#374151', marginBottom: '8px' }}>Cargando documentos DECA</h3>
          <p style={{ color: '#6b7280', margin: 0 }}>Obteniendo la lista de documentos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="header">
          <span className="badge">DECA</span>
        </div>
        <h1 className="title">Documentos DECA</h1>
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 32px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #fecaca'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '32px'
          }}>
            ⚠️
          </div>
          <h3 style={{ color: '#dc2626', marginBottom: '16px', fontSize: '20px', fontWeight: 600 }}>
            Error al cargar documentos
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '32px', fontSize: '16px' }}>{error}</p>
          <button 
            className="button button-primary"
            onClick={() => window.location.reload()}
            style={{ padding: '12px 32px', fontSize: '16px' }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <span className="badge">DECA</span>
      </div>
      
      {/* HEADER SECTION */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: 700, 
            color: '#111827', 
            margin: 0, 
            marginBottom: '8px' 
          }}>
            Documentos DECA
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#6b7280', 
            margin: 0 
          }}>
            Gestión de Documentos Electrónicos de Control Administrativo
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '8px 16px',
            background: '#f8fafc',
            borderRadius: '12px',
            fontSize: '14px',
            color: '#374151'
          }}>
            <span style={{ fontWeight: 600 }}>Total:</span>
            <span style={{ 
              background: '#3b82f6', 
              color: 'white', 
              padding: '4px 12px', 
              borderRadius: '20px',
              fontWeight: 600,
              fontSize: '13px'
            }}>
              {filteredDocuments.length}
            </span>
          </div>
          
          <button 
            onClick={() => navigate('/generar-deca')}
            style={{ 
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              padding: '16px 32px', 
              fontSize: '16px', 
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 35px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.3)';
            }}
          >
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px'
            }}>
              +
            </div>
            Generar DECA
          </button>
        </div>
      </div>

      {/* FILTERS SECTION */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        marginBottom: '32px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'minmax(300px, 1fr) auto', 
          gap: '16px',
          alignItems: 'end'
        }}>
          {/* Búsqueda */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: 600, 
              color: '#374151', 
              marginBottom: '8px' 
            }}>
              Buscar documentos
            </label>
            <input
              type="text"
              placeholder="Código, cargador, transportista, origen o destino..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '10px',
                fontSize: '14px',
                background: '#f9fafb',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Filtro por estado */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: 600, 
              color: '#374151', 
              marginBottom: '8px' 
            }}>
              Estado
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '10px',
                fontSize: '14px',
                background: 'white',
                outline: 'none',
                minWidth: '140px'
              }}
            >
              <option value="todos">Todos</option>
              <option value="generado">Generado</option>
              <option value="enviado">Enviado</option>
              <option value="completado">Completado</option>
            </select>
          </div>
        </div>
      </div>

      {/* CONTENT SECTION */}
      {filteredDocuments.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '60px 32px',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            width: '120px',
            height: '120px',
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 32px',
            fontSize: '48px'
          }}>
            📄
          </div>
          <h3 style={{ 
            fontSize: '24px', 
            fontWeight: 600, 
            marginBottom: '16px', 
            color: '#374151' 
          }}>
            {searchTerm || statusFilter !== 'todos' ? 'No se encontraron documentos' : 'No hay documentos DECA'}
          </h3>
          <p style={{ 
            color: '#6b7280', 
            marginBottom: '32px', 
            fontSize: '16px',
            maxWidth: '400px',
            margin: '0 auto 32px'
          }}>
            {searchTerm || statusFilter !== 'todos' 
              ? 'Intenta ajustar los filtros de búsqueda para encontrar documentos.'
              : 'Aún no se han generado documentos DECA para su empresa. Genere el primero para comenzar.'
            }
          </p>
          <button 
            onClick={() => navigate('/generar-deca')}
            style={{ 
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              padding: '18px 40px', 
              fontSize: '18px', 
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 12px 35px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '0 auto'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 16px 45px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 12px 35px rgba(59, 130, 246, 0.3)';
            }}
          >
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px'
            }}>
              +
            </div>
            {searchTerm || statusFilter !== 'todos' ? 'Generar Nuevo DECA' : 'Generar Primer DECA'}
          </button>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', 
          gap: '24px' 
        }}>
          {filteredDocuments.map((deca) => {
            const statusStyle = getStatusColor(deca.estado);
            return (
              <div 
                key={deca.id} 
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onClick={() => navigate(`/verificar-deca/${deca.id}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 25px -5px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                {/* Header de la card */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  marginBottom: '20px' 
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      fontSize: '18px', 
                      fontWeight: 700, 
                      marginBottom: '8px',
                      color: '#111827',
                      fontFamily: 'monospace'
                    }}>
                      {deca.codigoDECA || "(sin código)"}
                    </h3>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: 'center' }}>
                      <span style={{
                        background: statusStyle.bg,
                        color: statusStyle.color,
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {statusStyle.text}
                      </span>
                      <span style={{
                        color: '#9ca3af',
                        fontSize: '12px'
                      }}>
                        {formatDateTime(deca.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Información principal */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '16px', 
                  marginBottom: '20px' 
                }}>
                  <div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#9ca3af', 
                      marginBottom: '4px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Cargador
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: '#374151'
                    }}>
                      {deca.cargador?.nombre || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#9ca3af', 
                      marginBottom: '4px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Transportista
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: '#374151'
                    }}>
                      {deca.transportista?.nombre || 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Ruta */}
                <div style={{
                  background: '#f8fafc',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6b7280', 
                    marginBottom: '8px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Ruta
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                        {deca.origen?.ciudad || 'N/A'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        Origen
                      </div>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '2px',
                      background: '#d1d5db',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        right: '-4px',
                        top: '-3px',
                        width: 0,
                        height: 0,
                        borderLeft: '8px solid #d1d5db',
                        borderTop: '4px solid transparent',
                        borderBottom: '4px solid transparent'
                      }}></div>
                    </div>
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                        {deca.destino?.ciudad || 'N/A'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        Destino
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer de la card */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '16px',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#3b82f6', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>👁</span> Ver detalles
                  </div>
                  {deca.pdfUrl && (
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#10b981', 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>📄</span> PDF disponible
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}